import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Share2, Loader2, Instagram, Facebook, CheckCircle2, Link2, Unlink, ShieldAlert,
  Music2, MessageCircle, ArrowLeft, ArrowRight, ExternalLink, User, Building2, Copy, Download,
} from "lucide-react";
import DOMPurify from "dompurify";
import { api, apiError, BACKEND_URL, downloadFile } from "../../lib/api";
import { BackToCampaigns } from "../../components/campaign/BackToCampaigns";
import { Button } from "../../components/ui/button";

const NETWORKS = {
  facebook: { label: "Facebook", Icon: Facebook, color: "text-blue-400", personalUrl: "https://www.facebook.com/", meta: true },
  instagram: { label: "Instagram", Icon: Instagram, color: "text-pink-400", personalUrl: "https://www.instagram.com/", meta: true },
  tiktok: { label: "TikTok", Icon: Music2, color: "text-zinc-100", personalUrl: "https://www.tiktok.com/upload", meta: false },
};

const StepList = ({ steps }) => (
  <ol className="space-y-2.5 mt-3">
    {steps.map((s, i) => (
      <li key={s} className="flex gap-3 text-sm text-zinc-300">
        <span className="shrink-0 w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
        <span className="pt-0.5" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s) }} />
      </li>
    ))}
  </ol>
);

const OptionCard = ({ icon: Icon, title, badge, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#121212] p-6">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="text-purple-400" size={20} />
      <h3 className="font-display font-bold text-lg">{title}</h3>
      {badge && <span className="text-[10px] uppercase tracking-wider bg-purple-500/15 text-purple-300 px-2 py-0.5 rounded-full font-bold">{badge}</span>}
    </div>
    {children}
  </div>
);

const OpenLink = ({ href, children, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className="inline-flex items-center gap-2 mt-4 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-5 py-3 text-sm transition-colors">
    {children} <ExternalLink size={15} />
  </a>
);

const QuickContent = ({ campaign }) => {
  if (campaign === undefined) {
    return <div className="mt-5 flex items-center gap-2 text-xs text-zinc-500"><Loader2 className="animate-spin" size={14} /> Buscando tu última campaña...</div>;
  }
  if (!campaign) {
    return (
      <div className="mt-5 rounded-xl border border-white/10 bg-black/40 p-4 text-sm text-zinc-400">
        Aún no tienes una campaña. <Link to="/dashboard/campanas" className="text-purple-400 underline">Crea tu primera campaña</Link> y aquí podrás copiar su contenido al instante.
      </div>
    );
  }
  const text = campaign.text?.text || "";
  const slug = campaign.funnel_slug;
  const caption = slug ? `${text}\n\n👉 ${window.location.origin}/f/${slug}` : text;
  const images = campaign.images || [];
  const videos = (campaign.videos || []).filter((v) => v.status === "done" && v.video_url);
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("¡Texto copiado! Pégalo en tu publicación.");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = caption; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("¡Texto copiado! Pégalo en tu publicación.");
      } catch {
        toast.error("No se pudo copiar. Selecciona el texto manualmente.");
      }
    }
  };
  return (
    <div data-testid="quick-content" className="mt-5 rounded-xl border border-purple-500/30 bg-[#0e0a16] p-4">
      <p className="text-xs uppercase tracking-wider text-purple-300 font-bold mb-2">⚡ Atajo: tu última campaña — {campaign.title}</p>
      {text ? (
        <button data-testid="quick-copy-text" onClick={copyText} className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors">
          <Copy size={15} /> Copiar texto {slug ? "y enlace" : ""}
        </button>
      ) : <p className="text-xs text-zinc-500">Esta campaña aún no tiene texto generado.</p>}
      {(images.length > 0 || videos.length > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
          {images.map((img, i) => (
            <div key={img.content_id} className="rounded-lg border border-white/10 overflow-hidden bg-black">
              <img src={`${BACKEND_URL}${img.image_url}`} alt="" className="w-full h-16 object-cover" />
              <button data-testid="quick-dl-image" onClick={() => downloadFile(`${BACKEND_URL}${img.image_url}`, `multicontactos-imagen-${i + 1}.jpg`)} className="w-full flex items-center justify-center gap-1 text-[10px] py-1.5 bg-white/5 hover:bg-purple-600 transition-colors"><Download size={11} /> Imagen</button>
            </div>
          ))}
          {videos.map((v, i) => (
            <div key={v.content_id} className="rounded-lg border border-white/10 overflow-hidden bg-black">
              <video src={v.video_url} className="w-full h-16 object-cover" />
              <button data-testid="quick-dl-video" onClick={() => downloadFile(v.video_url, `multicontactos-video-${i + 1}.mp4`)} className="w-full flex items-center justify-center gap-1 text-[10px] py-1.5 bg-white/5 hover:bg-purple-600 transition-colors"><Download size={11} /> Video</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PersonalGuide = ({ label, url, testid, campaign }) => (
  <OptionCard icon={User} title="Usar mi cuenta personal" testid={testid}>
    <p className="text-sm text-zinc-400">La forma más rápida: copia tu campaña ya terminada y publícala tú mismo.</p>
    <StepList steps={[
      'Copia el texto de tu campaña con el botón de abajo (o desde <a href="/dashboard/campanas" class="text-purple-400 underline">Campañas</a>) y descarga la imagen/video.',
      `Abre ${label} con el botón de abajo.`,
      "Crea una nueva publicación.",
      "Pega el texto y sube la imagen o el video que descargaste.",
      "Publica. ¡Listo, tu campaña ya está en línea!",
    ]} />
    <QuickContent campaign={campaign} />
    <OpenLink href={url} testid={`open-${testid}`}>Abrir {label}</OpenLink>
  </OptionCard>
);

export default function SocialConnect() {
  const [status, setStatus] = useState(null); // null=loading
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null); // 'facebook' | 'instagram' | 'tiktok' | 'whatsapp'
  const [latest, setLatest] = useState(undefined); // undefined=loading, null=none, obj=latest campaign
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/meta/status").then((r) => setStatus(r.data)).catch((err) => {
      console.error("meta status error:", err);
      setStatus({ connected: false });
    });
  }, []);

  useEffect(() => {
    load();
    api.get("/guided/campaigns").then((r) => setLatest(r.data?.[0] || null)).catch(() => setLatest(null));
  }, [load]);

  useEffect(() => {
    const m = params.get("meta");
    if (!m) return;
    if (m === "connected") { toast.success("¡Cuenta de Meta conectada!"); setSelected("facebook"); }
    if (m === "error") toast.error("No se pudo conectar con Meta. Inténtalo de nuevo.");
    params.delete("meta");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/meta/login?origin=${encodeURIComponent(window.location.origin)}`);
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/meta/disconnect"); toast.success("Cuenta desconectada"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const selectPage = async (page_id) => {
    try {
      await api.post(`/meta/select-page?page_id=${page_id}`);
      setStatus((s) => ({ ...s, primary_page_id: page_id }));
      toast.success("Página seleccionada");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (status === null) {
    return (
      <div data-testid="social-page" className="max-w-3xl">
        <BackToCampaigns />
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-500" size={32} /></div>
      </div>
    );
  }

  // -------- Vista de selección (paso 1) --------
  if (!selected) {
    return (
      <div data-testid="social-page" className="max-w-3xl">
        <BackToCampaigns />
        <div className="flex items-center gap-3 mb-2">
          <Share2 className="text-purple-500" size={26} />
          <h1 className="font-display font-black tracking-tight text-3xl">Redes Sociales</h1>
        </div>
        <p className="text-zinc-400 mb-8">Te guiamos paso a paso para publicar tu campaña. <strong className="text-zinc-200">1.</strong> Elige dónde quieres publicar:</p>

        <div className="grid sm:grid-cols-3 gap-4">
          {Object.entries(NETWORKS).map(([key, n]) => (
            <button key={key} data-testid={`select-${key}`} onClick={() => setSelected(key)}
              className="text-left rounded-2xl border border-white/10 bg-[#121212] p-6 hover:border-purple-500/60 hover:-translate-y-0.5 transition-all">
              <n.Icon className={n.color} size={28} />
              <h3 className="font-display font-bold text-lg mt-3 flex items-center justify-between">{n.label} <ArrowRight size={16} className="text-zinc-500" /></h3>
              <p className="text-sm text-zinc-400 mt-1">Cuenta personal o de empresa.</p>
            </button>
          ))}
        </div>

        <button data-testid="select-whatsapp" onClick={() => setSelected("whatsapp")}
          className="mt-4 w-full text-left rounded-2xl border border-green-500/30 bg-green-500/5 p-6 hover:border-green-500/60 transition-all flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageCircle className="text-green-400" size={26} />
            <div>
              <h3 className="font-display font-bold text-lg">Publicar por WhatsApp</h3>
              <p className="text-sm text-zinc-400">Envía tu campaña terminada desde tu WhatsApp personal.</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-zinc-500" />
        </button>
      </div>
    );
  }

  const backBtn = (
    <button data-testid="social-back" onClick={() => setSelected(null)} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-4">
      <ArrowLeft size={16} /> Elegir otra red
    </button>
  );

  // -------- WhatsApp --------
  if (selected === "whatsapp") {
    return (
      <div data-testid="social-page" className="max-w-3xl">
        <BackToCampaigns />
        {backBtn}
        <div className="flex items-center gap-3 mb-6">
          <MessageCircle className="text-green-400" size={26} />
          <h1 className="font-display font-black tracking-tight text-2xl">Publicar por WhatsApp</h1>
        </div>
        <OptionCard icon={User} title="Enviar desde mi WhatsApp personal" testid="whatsapp-personal">
          <p className="text-sm text-zinc-400">Comparte tu campaña con tus contactos, grupos y estados de WhatsApp.</p>
          <StepList steps={[
            'Abre tu campaña en <a href="/dashboard/campanas" class="text-purple-400 underline">Campañas</a> y copia el <strong>texto</strong> y descarga la <strong>imagen/video</strong>.',
            "Abre WhatsApp con el botón de abajo (Web o tu teléfono).",
            "Elige un contacto, grupo o tu Estado.",
            "Pega el texto y adjunta la imagen o el video.",
            "Envía. Repite con cada prospecto. ¡Así de simple!",
          ]} />
          <QuickContent campaign={latest} />
          <OpenLink href="https://web.whatsapp.com/" testid="open-whatsapp">Abrir WhatsApp Web</OpenLink>
        </OptionCard>
      </div>
    );
  }

  // -------- Facebook / Instagram / TikTok --------
  const n = NETWORKS[selected];
  return (
    <div data-testid="social-page" className="max-w-3xl">
      <BackToCampaigns />
      {backBtn}
      <div className="flex items-center gap-3 mb-2">
        <n.Icon className={n.color} size={26} />
        <h1 className="font-display font-black tracking-tight text-2xl">Publicar en {n.label}</h1>
      </div>
      <p className="text-zinc-400 mb-6"><strong className="text-zinc-200">2.</strong> Elige cómo quieres publicar:</p>

      <div className="space-y-5">
        <PersonalGuide label={n.label} url={n.personalUrl} testid={`${selected}-personal`} campaign={latest} />

        {n.meta ? (
          <OptionCard icon={Building2} title="Usar Meta Business Suite" badge="Empresas" testid={`${selected}-business`}>
            <p className="text-sm text-zinc-400">
              <strong className="text-zinc-200">¿Qué es?</strong> Meta Business Suite es la herramienta <strong>gratuita</strong> de Meta para administrar y programar
              publicaciones de tu Página de Facebook e Instagram desde un solo lugar, ver estadísticas y responder mensajes.
            </p>
            <p className="text-sm text-zinc-400 mt-3">
              <strong className="text-zinc-200">¿Cómo obtenerla?</strong> Necesitas una <strong>Página de Facebook</strong> y, para Instagram, una <strong>cuenta Profesional</strong> (Business o Creator)
              vinculada a esa Página. Crea o administra tu Business Suite en el enlace de abajo (es un trámite gratuito y rápido en el sitio de Meta).
            </p>
            <OpenLink href="https://business.facebook.com/" testid={`open-${selected}-business`}>Abrir Meta Business Suite</OpenLink>

            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="flex items-center gap-2 mb-1">
                <Link2 className="text-yellow-impact" size={16} />
                <h4 className="font-semibold text-zinc-100">Publicar automáticamente <span className="text-[10px] uppercase tracking-wider bg-yellow-400/15 text-yellow-impact px-2 py-0.5 rounded-full font-bold ml-1">Avanzado</span></h4>
              </div>
              <p className="text-sm text-zinc-400 mb-3">Conecta tu cuenta de Meta y publicaremos por ti, sin copiar ni pegar.</p>
              {!status.connected ? (
                <Button data-testid="connect-meta" onClick={connect} disabled={busy} className="bg-[#1877F2] hover:bg-[#1568d8] text-white font-bold rounded-xl px-5 py-3 text-sm">
                  {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Link2 size={16} className="mr-2" />} Conectar con Facebook / Instagram
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-4 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="text-green-400" size={18} />
                      <span className="font-semibold text-green-400">Conectado como {status.name || "tu cuenta"}</span>
                    </div>
                    <Button data-testid="disconnect-meta" onClick={disconnect} disabled={busy} variant="outline" size="sm" className="border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-full">
                      <Unlink size={14} className="mr-1.5" /> Desconectar
                    </Button>
                  </div>
                  {status.pages?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-zinc-500">Elige la Página (y su Instagram) para tus campañas:</p>
                      {status.pages.map((p) => (
                        <button key={p.page_id} data-testid="meta-page-option" onClick={() => selectPage(p.page_id)}
                          className={`w-full text-left rounded-xl border p-3 transition-all ${status.primary_page_id === p.page_id ? "border-purple-500 bg-purple-500/10" : "border-white/10 bg-black hover:border-purple-500/50"}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm flex items-center gap-2"><Facebook size={14} className="text-blue-400" /> {p.page_name}</span>
                            {status.primary_page_id === p.page_id && <CheckCircle2 size={16} className="text-purple-400" />}
                          </div>
                          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5">
                            <Instagram size={12} className={p.has_ig ? "text-pink-400" : "text-zinc-600"} />
                            {p.has_ig ? `Instagram: @${p.ig_username}` : "Sin Instagram vinculado (solo Facebook)"}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="rounded-xl border border-yellow-400/25 bg-yellow-400/5 p-3 flex items-start gap-2">
                    <ShieldAlert className="text-yellow-400 mt-0.5 shrink-0" size={16} />
                    <p className="text-xs text-zinc-300">Mientras la app esté en revisión de Meta, la publicación automática funciona con tu propia cuenta y las cuentas de prueba autorizadas.</p>
                  </div>
                </div>
              )}
            </div>
          </OptionCard>
        ) : (
          <OptionCard icon={Building2} title="Usar TikTok para Empresas" badge="Empresas" testid="tiktok-business">
            <p className="text-sm text-zinc-400">
              <strong className="text-zinc-200">¿Qué es?</strong> Una <strong>cuenta de TikTok para Empresas</strong> (Business) te da acceso gratuito a estadísticas detalladas,
              música comercial sin derechos, enlaces en tu perfil y herramientas de promoción/anuncios.
            </p>
            <p className="text-sm text-zinc-400 mt-3">
              <strong className="text-zinc-200">¿Cómo obtenerla?</strong> En la app de TikTok ve a <strong>Perfil → Menú → Ajustes y privacidad → Cuenta → Cambiar a cuenta de empresa</strong>.
              Es gratis. También puedes conocer todas las herramientas para empresas en el enlace de abajo.
            </p>
            <OpenLink href="https://www.tiktok.com/business/es" testid="open-tiktok-business">Conocer TikTok para Empresas</OpenLink>
          </OptionCard>
        )}
      </div>
    </div>
  );
}
